import { useEffect, useState } from "react";

import * as Api from "@/api";
import { NEW_TOKEN } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputCopy from "@/design/forms/InputCopy";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Valkyrie, {
  viArrowLeft,
  viArrowsRotateRight,
  viKey,
  viSpinnerThird,
  viTrashCan,
} from "@sippy-platform/valkyrie";

import {
  Alert,
  Button,
  Dialog,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import tokenReducer from "./tokenReducer";

function TokenDialog({ tokenId, closeDialog }) {
  const {
    value: token,
    dispatch,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: {},
    mode: SaveMode.MANUAL,
    reducer: tokenReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      description: string(),
    }),
    onSubmit: () => Api.storeToken(token),
    onSuccess: (response) => {
      if (!token.id) {
        dispatch({ type: "postToken", value: response.data.token });
      }

      Promise.resolve(Api.getTokens());
    },
  });

  useEffect(() => {
    if (tokenId === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_TOKEN });
      return;
    }

    Promise.resolve(Api.getToken({ id: Number(tokenId) })).then((response) =>
      dispatch({ type: "set", value: response.data.token }),
    );
  }, [dispatch, tokenId]);

  useEffect(() => {
    dispatch({ type: "set", value: tokenId === "new" ? {} : tokenId });
  }, [dispatch, tokenId]);

  const formHandler = (property, value) => {
    dispatch({ type: property, value });
  };

  // Save
  const [savingToken, setSavingToken] = useState(false);

  // Regenerate token
  const [regenerateFlow, setRegenerateFlow] = useState(false);

  function generateToken() {
    setSavingToken(true);

    Promise.resolve(Api.generateToken(token))
      .then((response) => {
        dispatch({ type: "token", value: response.data.token.token });
        setRegenerateFlow(false);

        Promise.resolve(Api.getTokens());
      })
      .finally(() => setSavingToken(false));
  }

  // Delete token
  const [deleteFlow, setDeleteFlow] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  function deleteToken() {
    setSavingToken(true);

    Promise.resolve(Api.deleteToken(token))
      .then(() => {
        closeDialog();
        setDeleteFlow(false);
        setConfirmName("");

        Promise.resolve(Api.getTokens());
      })
      .finally(() => setSavingToken(false));
  }

  return (
    <Dialog open={!!tokenId} onClose={closeDialog}>
      {token?.id ? (
        <HeaderCard
          avatar={<Avatar text={token.name} icon={viKey} />}
          title={token.name || "Unnamed token"}
          action={
            !deleteFlow &&
            !regenerateFlow && (
              <IconButton onClick={() => setDeleteFlow(true)}>
                <Valkyrie icon={viTrashCan} />
              </IconButton>
            )
          }
        />
      ) : (
        <HeaderCard avatar={<Avatar text="New token" icon={viKey} />} title="New token" />
      )}
      <Stack
        sx={{
          gap: 2,
          mt: 2,
        }}
      >
        {regenerateFlow ? (
          <>
            <FormGroup title="Confirm regeneration">
              <Grid size={12}>
                <Typography>
                  Are you sure you want to regenerate the token? Be aware that any application using this token will
                  need to be updated as the current token will no longer be valid.
                </Typography>
              </Grid>
            </FormGroup>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setRegenerateFlow(false)}
                startIcon={<Valkyrie icon={viArrowLeft} />}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => generateToken()}
                startIcon={<Valkyrie icon={savingToken ? viSpinnerThird : viArrowsRotateRight} spin={savingToken} />}
              >
                Regenerate token
              </Button>
            </Stack>
          </>
        ) : deleteFlow ? (
          <>
            <FormGroup title="Confirm deletion">
              <Grid size={12}>
                <Typography>
                  Are you sure you want to delete the token? Any application using it will no longer work. Confirm the
                  name of the token to delete it.
                </Typography>
              </Grid>
              <Grid size={12}>
                <InputText
                  required
                  id="name"
                  label="Confirm token name"
                  onChange={(id, value) => setConfirmName(value)}
                  value={confirmName}
                />
              </Grid>
            </FormGroup>
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() => setDeleteFlow(false)}
                startIcon={<Valkyrie icon={viArrowLeft} />}
              >
                Back
              </Button>
              <Button
                disabled={token.name !== confirmName}
                variant="contained"
                color="error"
                onClick={() => deleteToken()}
                startIcon={<Valkyrie icon={savingToken ? viSpinnerThird : viTrashCan} spin={savingToken} />}
              >
                Delete token
              </Button>
            </Stack>
          </>
        ) : (
          <>
            {token?.token && (
              <Alert sx={{ boxShadow: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  This is your new token, keep it safe, you won't be able to see it again.
                </Typography>
                <InputCopy id="token" label="API token" value={token.token} />
              </Alert>
            )}
            <FormGroup title="Settings">
              <Grid size={12}>
                <InputText required id="name" label="Name" onChange={formHandler} value={token.name} error={errors} />
              </Grid>
              <Grid size={12}>
                <InputText
                  id="description"
                  label="Description"
                  multiline
                  rows={4}
                  onChange={formHandler}
                  value={token.description}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            {token.id && (
              <FormGroup title="Manage token">
                <Grid size={12}>
                  <List>
                    <ListItem sx={{ p: 0, minHeight: 48 }}>
                      <ListItemButton sx={{ py: 0.5, minHeight: 48 }} onClick={() => setRegenerateFlow(true)}>
                        <ListItemIcon>
                          <Valkyrie icon={viArrowsRotateRight} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Regenerate token"
                          secondary="Invalidate the token and request a new one."
                        />
                      </ListItemButton>
                    </ListItem>
                    <ListItem sx={{ p: 0, minHeight: 48 }}>
                      <ListItemButton sx={{ py: 0.5, minHeight: 48 }} onClick={() => setDeleteFlow(true)}>
                        <ListItemIcon>
                          <Valkyrie icon={viTrashCan} />
                        </ListItemIcon>
                        <ListItemText primary="Delete token" secondary="Delete and invalidate the token." />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Grid>
              </FormGroup>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: "flex-end",
                gap: 1,
                containerType: "inline-size",
              }}
            >
              <Button variant="outlined" onClick={closeDialog}>
                Cancel
              </Button>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
            </Stack>
          </>
        )}
      </Stack>
    </Dialog>
  );
}

TokenDialog.propTypes = {
  closeDialog: PropTypes.func,
  tokenId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TokenDialog;
